/*
	Colourway row slider.

	The track is a native scroll container -- it already scrolls by drag, wheel
	and keyboard with no JS at all. This element only adds the arrow affordance
	on top: it shows the buttons when there is somewhere to scroll, hides them
	when there isn't, and disables each one at its end of the track.

	Deliberately not Swiper: this row renders on the product page inside a
	sticky column, and a carousel library here would cost more than the feature.
*/

class LinkedColorways extends HTMLElement {
	connectedCallback() {
		this.track = this.querySelector('.linked-row__track');
		this.prev = this.querySelector('[data-linked-prev]');
		this.next = this.querySelector('[data-linked-next]');
		if (!this.track) return;

		this.onScroll = this.onScroll.bind(this);
		this.update = this.update.bind(this);

		this.prev?.addEventListener('click', () => this.scrollByPage(-1));
		this.next?.addEventListener('click', () => this.scrollByPage(1));
		this.track.addEventListener('scroll', this.onScroll, { passive: true });

		// Images are lazy, so the track's scrollWidth grows after first paint.
		if ('ResizeObserver' in window) {
			this.ro = new ResizeObserver(this.update);
			this.ro.observe(this.track);
		}
		window.addEventListener('resize', this.update);
		this.update();
	}

	disconnectedCallback() {
		this.ro?.disconnect();
		window.removeEventListener('resize', this.update);
	}

	/** One page = the visible width minus a tile, so the edge tile stays as an anchor. */
	scrollByPage(direction) {
		const tile = this.querySelector('.linked-row__item');
		const step = tile ? tile.getBoundingClientRect().width + this.gap() : 120;
		const page = Math.max(step, this.track.clientWidth - step);
		this.track.scrollBy({ left: page * direction, behavior: this.motionOK() ? 'smooth' : 'auto' });
	}

	gap() {
		const g = parseFloat(getComputedStyle(this.track).columnGap);
		return Number.isFinite(g) ? g : 0;
	}

	motionOK() {
		return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	onScroll() {
		// Coalesce scroll events into one paint.
		if (this.raf) return;
		this.raf = requestAnimationFrame(() => {
			this.raf = null;
			this.update();
		});
	}

	update() {
		const { scrollLeft, scrollWidth, clientWidth } = this.track;
		// 2px tolerance: sub-pixel layout means scrollLeft rarely hits the exact end.
		const overflowing = scrollWidth - clientWidth > 2;
		const atStart = scrollLeft <= 2;
		const atEnd = scrollLeft >= scrollWidth - clientWidth - 2;

		this.toggleAttribute('data-scrollable', overflowing);
		if (this.prev) {
			this.prev.disabled = atStart;
			this.prev.hidden = !overflowing;
		}
		if (this.next) {
			this.next.disabled = atEnd;
			this.next.hidden = !overflowing;
		}
		// Fade only where there is more to see.
		this.toggleAttribute('data-fade-start', overflowing && !atStart);
		this.toggleAttribute('data-fade-end', overflowing && !atEnd);
	}
}

if (!customElements.get('linked-colorways')) {
	customElements.define('linked-colorways', LinkedColorways);
}
